// Library methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Types
import { EMRConnectionType } from "src/services/emr/types";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export type JobStatus = "Working" | "Failing" | "Off" | "Pending";
export type JobDirection = "In" | "Out";

export const getJobStatus = (
  connection: EMRConnectionType | undefined,
  hubIsActive: boolean
): JobStatus => {
  if (!connection || !connection.enabled) return "Off";
  if (!hubIsActive) return "Pending";
  if (connection.health?.state === "Failing") return "Failing";
  if (connection.health?.state === "Ok") return "Working";
  return "Pending";
};

const STATUS_COLORS: Record<JobStatus, { color: string; background: string }> =
  {
    Working: { color: "#0B3B30", background: "#7FD9C2" },
    Pending: { color: "#23253A", background: "#D5D7E0" },
    Failing: { color: "#4A0F0F", background: "#FFB4B4" },
    Off: { color: "inherit", background: "transparent" },
  };

const DirectionIcon = ({ direction }: { direction: JobDirection }) => {
  const { t } = useTranslation();

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={direction === "In" ? "#1B8F76" : "#8A8DA2"}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={direction === "In" ? t("In") : t("Out")}
      style={{ flexShrink: 0 }}
    >
      {direction === "In" ? (
        <path d="M12 4v13M6 11l6 6 6-6" />
      ) : (
        <path d="M12 20V7M6 13l6-6 6 6" />
      )}
    </svg>
  );
};

type JobRowType = {
  testId?: string;
  direction: JobDirection;
  title: string;
  description: ReactNode;
  status: JobStatus;
  badge?: string;
  action?: ReactNode;
};

const JobRow = ({
  testId,
  direction,
  title,
  description,
  status,
  badge,
  action,
}: JobRowType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      data-testid={testId}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        padding: "8px 10px",
        borderRadius: "6px",
        backgroundColor: isDark ? "#24263A" : "rgba(0, 0, 0, 0.05)",
        containerType: "inline-size",
      }}
    >
      <Box
        sx={{
          display: "grid",
          alignItems: "center",
          columnGap: "10px",
          rowGap: "2px",
          minHeight: "28px",
          gridTemplateColumns: "20px auto minmax(0, 1fr) auto auto",
          gridTemplateAreas: '"icon title description badge status"',
          "@container (max-width: 440px)": {
            gridTemplateColumns: "20px auto minmax(0, 1fr) auto",
            gridTemplateAreas:
              '"icon title . status" ". badge badge badge" ". description description description"',
          },
        }}
      >
        <Box display="flex" sx={{ gridArea: "icon" }}>
          <DirectionIcon direction={direction} />
        </Box>
        <Typography
          fontSize="13px"
          fontWeight={700}
          color={onSurface}
          sx={{
            gridArea: "title",
            minWidth: 0,
            "@container (min-width: 441px)": { minWidth: "64px" },
          }}
        >
          {title}
        </Typography>
        <Typography
          fontSize="12px"
          color={mutedText}
          lineHeight={1.35}
          sx={{ gridArea: "description", minWidth: 0, wordBreak: "break-word" }}
        >
          {description}
          {action}
        </Typography>
        {badge && (
          <Typography
            fontSize="11px"
            fontWeight={800}
            sx={{
              gridArea: "badge",
              justifySelf: "start",
              color: "#3B1F0B",
              backgroundColor: "#FFC48A",
              borderRadius: "999px",
              padding: "2px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {badge}
          </Typography>
        )}
        <Typography
          fontSize="11px"
          fontWeight={700}
          sx={{
            gridArea: "status",
            justifySelf: "end",
            color: status === "Off" ? mutedText : STATUS_COLORS[status].color,
            backgroundColor: STATUS_COLORS[status].background,
            border:
              status === "Off"
                ? `1px solid ${mutedText}`
                : "1px solid transparent",
            borderRadius: "999px",
            padding: "2px 8px",
            whiteSpace: "nowrap",
          }}
        >
          {t(status)}
        </Typography>
      </Box>
    </Box>
  );
};

export default JobRow;
